import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Boxes, Loader2, Plus, Minus, AlertTriangle, PackageCheck, Check, Users, User, PackagePlus, Pencil, Skull } from "lucide-react";

const CAT_COLOR = {
  weapon: "text-rose-500", ammo: "text-orange-500", food: "text-amber-500", water: "text-sky-500",
  medical: "text-emerald-500", fuel: "text-yellow-600", gear: "text-indigo-400", currency: "text-green-500", misc: "text-slate-400",
  kit: "text-fuchsia-500", kit_content: "text-fuchsia-500", kit_refill: "text-fuchsia-500",
};

function fmt(n) {
  const f = Number(n);
  return Number.isInteger(f) ? f : f.toFixed(1);
}

export default function GearSyncDialog({ gear, setGear, onApply, busy, characters = [], sources = [], owners = [], onMarkKia, onDismissKia }) {
  const [editId, setEditId] = useState(null);
  if (!gear) return null;
  const results = gear.results || [];
  const applyTo = gear.applyTo || [];

  const shared = results.filter((r) => r.shared);
  const personal = results.filter((r) => !r.shared);
  const orderPersonal = [...personal].sort((a, b) => (a.already_synced === b.already_synced ? 0 : a.already_synced ? 1 : -1));

  const keptShared = shared.filter((r) => r.keep).length;
  const keptPersonal = personal.filter((r) => r.keep).length;
  const keptCount = keptShared + keptPersonal;
  const allOn = keptCount === results.length && results.length > 0;
  const newCount = results.filter((r) => !r.already_synced).length;
  const syncedCount = results.length - newCount;

  const needsRecipients = keptShared > 0 && applyTo.length === 0;
  const totalChanges = keptPersonal + keptShared * Math.max(0, applyTo.length);

  const toggleChar = (c) => setGear((g) => {
    const cur = g.applyTo || [];
    const has = cur.some((x) => x.id === c.id);
    return { ...g, applyTo: has ? cur.filter((x) => x.id !== c.id) : [...cur, { id: c.id, name: c.name }] };
  });
  const setAllChars = (all) => setGear((g) => ({ ...g, applyTo: all ? characters.map((c) => ({ id: c.id, name: c.name })) : [] }));
  const toggle = (id, val) =>
    setGear((g) => ({ ...g, results: g.results.map((r) => (r.id === id ? { ...r, keep: val } : r)) }));
  const setArmoury = (id, val) =>
    setGear((g) => ({ ...g, results: g.results.map((r) => (r.id === id ? { ...r, armoury_name: val } : r)) }));
  const setField = (id, patch) =>
    setGear((g) => ({ ...g, results: g.results.map((r) => (r.id === id ? { ...r, ...patch } : r)) }));
  const toggleAll = (val) =>
    setGear((g) => ({ ...g, results: g.results.map((r) => ({ ...r, keep: val })) }));

  const renderRow = (r) => {
    const gain = r.action === "add";
    const cw = r.continuity_warning;
    return (
      <div key={r.id} data-testid={`gear-row-${r.id}`}
        className={`rounded-xl border p-3 transition-colors ${r.keep ? "border-border bg-card" : "border-border/50 bg-secondary/20 opacity-60"} ${cw ? "border-rose-500/50" : ""} ${r.already_synced ? "opacity-75" : ""}`}>
        <div className="flex items-start gap-3">
          <Checkbox checked={r.keep} onCheckedChange={(v) => toggle(r.id, !!v)}
            data-testid={`gear-keep-${r.id}`} className="mt-1" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${gain ? "bg-emerald-500/15 text-emerald-500" : "bg-rose-500/15 text-rose-500"}`}>
                {gain ? <Plus className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
              </span>
              <span className="font-semibold">{fmt(r.quantity)}{r.unit ? ` ${r.unit}` : ""} {r.name}</span>
              {r.already_synced ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground font-medium inline-flex items-center gap-1" data-testid={`gear-synced-${r.id}`}>
                  <Check className="w-3 h-3" /> Synced
                </span>
              ) : (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-semibold">New</span>
              )}
              <span className={`text-[10px] uppercase tracking-wide font-semibold ${CAT_COLOR[r.category] || CAT_COLOR.misc}`}>{r.category === "kit_content" ? "kit item" : r.category === "kit_refill" ? "kit refill" : r.category}</span>
              {r.kit && r.category !== "kit_refill" && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-400 font-medium inline-flex items-center gap-1">
                  <PackageCheck className="w-3 h-3" /> from {r.kit}
                </span>
              )}
              {r.confidence === "low" && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium">unsure</span>
              )}
              {!r.already_synced && (
                <button type="button" onClick={() => setEditId(editId === r.id ? null : r.id)}
                  data-testid={`gear-edit-${r.id}`}
                  className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 transition-colors ${editId === r.id ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-primary"}`}>
                  <Pencil className="w-3 h-3" /> {editId === r.id ? "Done" : "Edit"}
                </button>
              )}
            </div>
            {editId === r.id && (
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 rounded-lg border border-primary/30 bg-primary/[0.04] p-2.5" data-testid={`gear-edit-panel-${r.id}`}>
                {!r.shared && !r.kit_content && !r.kit_refill && (
                  <label className="flex flex-col gap-1 col-span-2 sm:col-span-1">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Owner</span>
                    <select value={r.owner} data-testid={`gear-edit-owner-${r.id}`}
                      onChange={(e) => setField(r.id, { owner: e.target.value, resolved_owner_id: (owners.find((o) => o.name === e.target.value) || {}).id || null })}
                      className="text-xs bg-background border border-border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary">
                      {[...new Set([r.owner, ...owners.map((o) => o.name)])].filter(Boolean).map((nm) => (
                        <option key={nm} value={nm}>{nm}</option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{gain ? "Count" : "Amount used"}</span>
                  <input type="number" min="0" step="1" value={r.quantity} data-testid={`gear-edit-qty-${r.id}`}
                    onChange={(e) => setField(r.id, { quantity: Math.max(0, Number(e.target.value) || 0) })}
                    className="text-xs bg-background border border-border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary" />
                </label>
                {!r.kit && r.category !== "kit_refill" && r.category !== "kit_content" && (
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Calibre</span>
                    <input value={r.calibre || ""} data-testid={`gear-edit-cal-${r.id}`}
                      onChange={(e) => setField(r.id, { calibre: e.target.value })}
                      placeholder="e.g. 338 / 9mm / arrows"
                      className="text-xs bg-background border border-border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary" />
                  </label>
                )}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-1">
              {r.category === "kit_refill" ? (
                <><span className="text-foreground/80 font-medium">{r.owner}</span>{" · "}<span className="text-primary font-medium">tops {r.kit} back to full</span></>
              ) : r.shared ? (
                <span className="text-primary font-medium">each selected character</span>
              ) : (
                <>
                  <span className="text-foreground/80 font-medium">{r.owner}</span>
                  {" · "}{fmt(r.current_qty)} → <span className={gain ? "text-emerald-500" : "text-rose-500"}>{fmt(r.new_qty)}</span>
                </>
              )}
            </div>
            {r.quote && (
              <div className="text-xs italic text-muted-foreground mt-1.5 border-l-2 border-border pl-2 line-clamp-2">"{r.quote}"</div>
            )}
            {gain && sources.length > 0 && r.category !== "kit_content" && r.category !== "kit_refill" && (
              <div className="flex items-center gap-1.5 mt-2" data-testid={`gear-source-${r.id}`}>
                <PackagePlus className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <label className="text-[11px] text-muted-foreground shrink-0">Draw from</label>
                <select
                  value={r.armoury_name || ""}
                  onChange={(e) => setArmoury(r.id, e.target.value)}
                  data-testid={`gear-source-select-${r.id}`}
                  className="text-xs bg-secondary/50 border border-border rounded-md px-2 py-1 flex-1 min-w-0 max-w-[220px] focus:outline-none focus:ring-1 focus:ring-primary">
                  <option value="">Auto — first available</option>
                  {sources.map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
            {cw && (
              <div className="flex items-center gap-1.5 text-xs text-rose-500 mt-1.5 font-medium" data-testid={`gear-continuity-${r.id}`}>
                <AlertTriangle className="w-3.5 h-3.5" />
                {cw.pool
                  ? `Continuity: fires ${fmt(cw.needed)} ${cw.calibre || ""} but only ${fmt(cw.available)} rounds in the pool`
                  : `Continuity: needs ${fmt(cw.needed)} but only had ${fmt(cw.available)}`}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && setGear(null)}>
      <DialogContent className="bg-popover max-w-xl max-h-[90vh] overflow-y-auto" data-testid="gear-sync-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Boxes className="w-5 h-5 text-primary" /> Sync gear
          </DialogTitle>
          <DialogDescription>
            {gear.loading ? "Scanning this scene…" : <>Changes found in <span className="font-semibold text-foreground">{gear.chapterTitle}</span>. Untick anything you don't want, then apply.</>}
          </DialogDescription>
        </DialogHeader>

        {gear.loading ? (
          <div className="flex flex-col items-center justify-center py-14 text-muted-foreground">
            <Loader2 className="w-8 h-8 animate-spin mb-3 text-primary" />
            Reading the scene for gear gained and used…
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="gear-toggle-all">
                <Checkbox checked={allOn} onCheckedChange={(v) => toggleAll(!!v)} />
                Select all items
              </label>
              <span className="text-xs text-muted-foreground">
                {newCount > 0 && <span className="text-emerald-500 font-medium">{newCount} new</span>}
                {newCount > 0 && syncedCount > 0 && " · "}
                {syncedCount > 0 && <span>{syncedCount} already synced</span>}
              </span>
            </div>

            {(gear.kiaSuggestions || []).length > 0 && (
              <div className="rounded-xl border border-destructive/50 bg-destructive/10 p-3 space-y-2" data-testid="gear-kia-suggestions">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-destructive">
                  <Skull className="w-3.5 h-3.5" /> Possible death detected
                </div>
                {gear.kiaSuggestions.map((s) => (
                  <div key={s.owner_id} data-testid={`gear-kia-suggestion-${s.owner_id}`}
                    className="rounded-lg border border-destructive/30 bg-background/60 p-2.5 space-y-2">
                    <p className="text-sm text-foreground">
                      <span className="font-semibold">{s.owner}</span> appears to be killed in this chapter.
                    </p>
                    {s.quote && <p className="text-xs text-muted-foreground italic">“{s.quote}”</p>}
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="destructive" className="h-7 gap-1.5" disabled={busy}
                        onClick={() => onMarkKia?.(s.owner_id)} data-testid={`gear-kia-confirm-${s.owner_id}`}>
                        <Skull className="w-3.5 h-3.5" /> Mark {s.owner} KIA
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7" disabled={busy}
                        onClick={() => onDismissKia?.(s.owner_id)} data-testid={`gear-kia-dismiss-${s.owner_id}`}>
                        Not dead
                      </Button>
                    </div>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">
                  Nothing is frozen until you confirm. Once marked KIA, you can loot their gear in Loadouts.
                </p>
              </div>
            )}

            {(gear.kiaFlags || []).length > 0 && (
              <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-3 space-y-1.5" data-testid="gear-kia-flags">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-destructive">
                  <Skull className="w-3.5 h-3.5" /> KIA continuity check
                </div>
                {gear.kiaFlags.map((f, i) => (
                  <p key={i} data-testid={`gear-kia-flag-${i}`} className="text-sm text-foreground">{f.note}</p>
                ))}
                <p className="text-xs text-muted-foreground">
                  These are not applied. If it's a flashback, ignore it — otherwise switch the character back to Active from the Codex.
                </p>
              </div>
            )}

            <ScrollArea className="max-h-[56vh] pr-3 -mr-3">
              <div className="space-y-4">
                {shared.length > 0 && (
                  <div className="space-y-2" data-testid="gear-shared-section">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-primary">
                      <Users className="w-3.5 h-3.5" /> Shared kit — issued to the team
                    </div>

                    {/* Recipient picker: who gets the shared kit */}
                    <div className={`rounded-lg border p-2.5 space-y-1.5 ${needsRecipients ? "border-amber-500/60 bg-amber-500/5" : "border-border bg-secondary/30"}`} data-testid="gear-applyto">
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] font-semibold text-foreground flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5" /> Who receives this kit?
                        </p>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setAllChars(true)} data-testid="gear-recipients-all" className="text-[11px] text-primary hover:underline">All</button>
                          <button type="button" onClick={() => setAllChars(false)} data-testid="gear-recipients-none" className="text-[11px] text-muted-foreground hover:underline">None</button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {characters.map((c) => {
                          const on = applyTo.some((x) => x.id === c.id);
                          return (
                            <button key={c.id} type="button" onClick={() => toggleChar(c)} data-testid={`gear-applyto-${c.id}`}
                              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50 text-muted-foreground"}`}>
                              {c.name}{c.nickname && c.nickname.trim() && c.nickname.trim().toLowerCase() !== (c.name || "").trim().toLowerCase() ? ` “${c.nickname.trim()}”` : ""}
                            </button>
                          );
                        })}
                      </div>
                      {needsRecipients ? (
                        <p className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">Pick at least one character to receive the shared kit.</p>
                      ) : applyTo.length > 0 ? (
                        <p className="text-[11px] text-primary">Each of the {applyTo.length} selected character{applyTo.length !== 1 ? "s" : ""} gets their own copy of the ticked shared items.</p>
                      ) : null}
                    </div>

                    {shared.map(renderRow)}
                  </div>
                )}

                {personal.length > 0 && (
                  <div className="space-y-2" data-testid="gear-personal-section">
                    {shared.length > 0 && (
                      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-muted-foreground pt-1">
                        <User className="w-3.5 h-3.5" /> Individual
                      </div>
                    )}
                    {orderPersonal.map(renderRow)}
                  </div>
                )}
              </div>
            </ScrollArea>

            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setGear(null)} disabled={busy} data-testid="gear-cancel">Cancel</Button>
              <Button onClick={onApply} disabled={busy || totalChanges === 0 || needsRecipients} data-testid="gear-apply-button" className="gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />}
                Apply {totalChanges} change{totalChanges !== 1 ? "s" : ""}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
